import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import { CalendarClock, ClipboardList, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/** Organisation-wide triage, not a second source of compliance status. */
export default function TrainingHubQueueSummary({ compact = false, onShowDetails } = {}) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE}/training/hub/review-queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Work queue is temporarily unavailable.');
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const evidence = data?.pending_evidence_reviews || [];
  const support = data?.support_requests || [];

  // Fetch continues in compact mode so incoming training reviews are not
  // silently hidden merely because the long manager work queue is closed.
  if (compact) {
    if (!error && !evidence.length && !support.length) return null;
    return (
      <section data-testid="training-queue-compact" role={error ? 'alert' : 'status'}
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span>{error
          ? 'Training review queue unavailable — check before signing off'
          : `${evidence.length} certificate${evidence.length === 1 ? '' : 's'} to review · ${support.length} training request${support.length === 1 ? '' : 's'}`}</span>
        {onShowDetails && <button type="button" onClick={onShowDetails}
          className="min-h-10 rounded-lg border border-amber-300 bg-white px-3 py-2 font-semibold">
          Open review queue
        </button>}
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="training-hub-queue">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold"><ClipboardList className="h-5 w-5 text-teal-700" /> Training &amp; Audit Hub · Work queue</h2>
          <p className="text-xs text-slate-500">Approve certificate evidence in the staff file; arrange requested training and practical assessments.</p>
        </div>
        <button type="button" className="rounded-lg border p-2" onClick={load} aria-label="Refresh training queue"><RefreshCw className="h-4 w-4" /></button>
      </div>
      {error && <p className="text-xs text-amber-800" role="alert">{error}</p>}
      {data && <div className="flex flex-wrap gap-3 text-sm">
        <span className="rounded-lg bg-amber-50 px-3 py-2">Evidence awaiting review: <strong>{evidence.length}</strong></span>
        <span className="rounded-lg bg-blue-50 px-3 py-2">Training &amp; competency requests: <strong>{support.length}</strong></span>
      </div>}
      {(evidence.length + support.length === 0 && data) && <p className="text-sm text-slate-600">No pending evidence reviews or support requests.</p>}
      {[...evidence.slice(0, 4).map(x => ({
        id: x.proposal_id, employeeId: x.employee_id, title: x.title, label: 'Review certificate'
      })), ...support.slice(0, 4).map(x => ({
        id: x.id, employeeId: x.employee_id,
        title: x.training_code?.replace(/_/g, ' '), label: x.status
      }))].map(item => (
        <button key={item.id} type="button" onClick={() => navigate(`/portal/employees/${item.employeeId}?tab=training`)} className="flex w-full justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm hover:bg-slate-50">
          <span className="min-w-0 truncate">{item.title || 'Training item'} <span className="text-slate-500">· {item.employeeId}</span></span>
          <span className="shrink-0 text-teal-700 flex gap-1 items-center"><CalendarClock className="h-3 w-3"/>{item.label}</span>
        </button>
      ))}
    </section>
  );
}